import * as pdfjsLib from 'pdfjs-dist'
import { MODEL_MODES } from '../../LLM/index.js'

// Configure PDF.js worker - match version with installed pdfjs-dist
// Use .mjs format for version 5.4.54+
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.mjs`

/**
 * Extract text content from PDF file
 * @param {File} pdfFile - The PDF file to extract text from
 * @returns {Promise<string>} - Extracted text content
 */
export const extractTextFromPDF = async (pdfFile) => {
  try {
    console.log('🔄 Extracting text from PDF:', pdfFile.name)
    console.log('📊 PDF file info:', {
      name: pdfFile.name,
      size: pdfFile.size,
      type: pdfFile.type,
      lastModified: pdfFile.lastModified
    })
    
    // Convert file to array buffer
    console.log('🔄 Converting file to array buffer...')
    const arrayBuffer = await pdfFile.arrayBuffer()
    console.log('✅ Array buffer created, size:', arrayBuffer.byteLength)
    
    // Load PDF document
    console.log('🔄 Loading PDF document...')
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
    console.log('✅ PDF document loaded successfully')
    
    let fullText = ''
    const totalPages = pdf.numPages
    
    console.log(`📄 PDF has ${totalPages} pages`)
    
    // Extract text from each page
    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      try {
        const page = await pdf.getPage(pageNum)
        const textContent = await page.getTextContent()
        
        // Combine text items into a single string
        const pageText = textContent.items
          .map(item => item.str)
          .join(' ')
          .trim()
        
        if (pageText) {
          fullText += `\n\n--- Halaman ${pageNum} ---\n${pageText}`
        }
        
        console.log(`✅ Extracted text from page ${pageNum}/${totalPages}`)
      } catch (pageError) {
        console.warn(`⚠️ Failed to extract text from page ${pageNum}:`, pageError)
        fullText += `\n\n--- Halaman ${pageNum} ---\n[Error: Gagal membaca halaman ini]`
      }
    }
    
    if (!fullText.trim()) {
      throw new Error('Tidak ada teks yang dapat diekstrak dari PDF. File mungkin berisi hanya gambar atau terenkripsi.')
    }
    
    console.log(`✅ Successfully extracted ${fullText.length} characters from PDF`)
    return fullText.trim()
    
  } catch (error) {
    console.error('❌ Error extracting text from PDF:', error)
    
    if (error.message.includes('Invalid PDF')) {
      throw new Error('File PDF tidak valid atau rusak.')
    } else if (error.message.includes('password')) {
      throw new Error('PDF terproteksi password. Silakan gunakan PDF yang tidak terenkripsi.')
    } else if (error.message.includes('Tidak ada teks')) {
      throw error // Re-throw our custom message
    } else {
      throw new Error('Gagal membaca file PDF. Pastikan file tidak rusak.')
    }
  }
}

/**
 * Analyze PDF content using LLM
 * @param {File} pdfFile - The PDF file to analyze
 * @param {string} userQuery - User's question or instruction
 * @param {Object} llmService - LLM service instance
 * @returns {Promise<Object>} - Analysis result
 */
export const analyzePDF = async (pdfFile, userQuery, llmService) => {
  try {
    console.log('🔄 Starting PDF analysis...')
    
    // Extract text from PDF
    const pdfText = await extractTextFromPDF(pdfFile)
    
    // Prepare the prompt for LLM
    const systemPrompt = `Anda adalah asisten AI yang ahli dalam menganalisis dokumen PDF. Anda akan diberikan konten teks dari sebuah file PDF dan pertanyaan/instruksi dari user.

Tugas Anda:
1. Baca dan pahami seluruh konten PDF dengan teliti
2. Jawab pertanyaan user berdasarkan informasi yang ada dalam PDF
3. Jika user meminta ringkasan, buatlah ringkasan yang komprehensif
4. Jika user meminta analisis, berikan analisis yang mendalam
5. Jika user meminta informasi spesifik, cari dan berikan informasi tersebut
6. Selalu berikan jawaban dalam bahasa Indonesia yang jelas dan terstruktur
7. Jika informasi yang diminta tidak ada dalam PDF, sampaikan hal tersebut dengan jelas

Format jawaban:
- Gunakan struktur yang jelas dengan heading dan bullet points jika diperlukan
- Berikan kutipan atau referensi ke bagian PDF yang relevan
- Jika ada data numerik, presentasikan dengan jelas
- Akhiri dengan kesimpulan jika diperlukan`

    const userPrompt = `Berikut adalah konten dari file PDF "${pdfFile.name}":

${pdfText}

---

Pertanyaan/Instruksi dari user: ${userQuery}

Silakan analisis konten PDF di atas dan jawab pertanyaan/instruksi user dengan lengkap dan akurat.`
    
    console.log('🔄 Sending to LLM for analysis...')
    
    // Use SMART model for multimodal capabilities
    const messages = [
      {
        role: 'system',
        content: systemPrompt
      },
      {
        role: 'user',
        content: userPrompt
      }
    ]
    
    const response = await llmService.sendMessage(messages, {
      mode: MODEL_MODES.SMART,
      additionalParams: {
        max_tokens: 3000,
        temperature: 0.7
      }
    })
    
    if (!response || typeof response !== 'string' || !response.trim()) {
      throw new Error('Tidak ada respons dari AI. Silakan coba lagi.')
    }
    
    const result = {
      fileName: pdfFile.name,
      fileSize: pdfFile.size,
      extractedTextLength: pdfText.length,
      userQuery: userQuery,
      analysis: response,
      generatedAt: new Date().toISOString(),
      mode: MODEL_MODES.SMART
    }
    
    console.log('✅ PDF analysis completed successfully')
    return result
    
  } catch (error) {
    console.error('❌ Error in PDF analysis:', error)
    
    if (error.message.includes('PDF')) {
      throw error // Re-throw PDF-specific errors
    } else if (error.message.includes('API')) {
      throw new Error('Gagal terhubung ke layanan AI. Silakan coba lagi.')
    } else if (error.message.includes('timeout')) {
      throw new Error('Analisis memakan waktu terlalu lama. Silakan coba dengan file PDF yang lebih kecil.')
    } else {
      throw new Error('Gagal menganalisis PDF. Silakan coba lagi.')
    }
  }
}

/**
 * Validate PDF file before processing
 * @param {File} file - File to validate
 * @returns {Object} - Validation result
 */
export const validatePDFFile = (file) => {
  const maxSize = 50 * 1024 * 1024 // 50MB
  const allowedTypes = ['application/pdf']
  
  if (!file) {
    return {
      isValid: false,
      error: 'File tidak ditemukan'
    }
  }
  
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Hanya file PDF yang diperbolehkan'
    }
  }
  
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'Ukuran file terlalu besar. Maksimal 50MB'
    }
  }
  
  return {
    isValid: true,
    error: null
  }
}

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}